package com.example.borshi.web.wallet

import kotlin.math.roundToLong

/**
 * Money Value Object - Prevents floating point errors by using minor units (paise).
 */
data class Money(
    val currency: String = "INR",
    val minorUnits: Long = 0L
) : Comparable<Money> {
    init {
        require(minorUnits >= 0) { "Money amount cannot be negative" }
    }

    companion object {
        fun fromRupees(amount: Double, currency: String = "INR"): Money =
            Money(currency = currency, minorUnits = (amount * 100).roundToLong())
        fun fromPaise(amount: Long, currency: String = "INR"): Money =
            Money(currency = currency, minorUnits = amount)
        fun zero(currency: String = "INR"): Money = Money(currency = currency, minorUnits = 0L)
    }

    operator fun plus(other: Money): Money = copy(minorUnits = this.minorUnits + other.minorUnits)
    operator fun minus(other: Money): Money = copy(minorUnits = this.minorUnits - other.minorUnits)
    override fun compareTo(other: Money): Int = this.minorUnits.compareTo(other.minorUnits)

    fun format(): String {
        val rupeesPart = minorUnits / 100
        val paisePart = (minorUnits % 100).toString().padStart(2, '0')
        return "₹$rupeesPart.$paisePart"
    }
}

/**
 * Wallet Entity - Handles the core logic of balance and reserves.
 */
data class Wallet(
    val userId: String,
    val availableBalance: Money,
    val reservedBalance: Money,
    val currency: String = "INR",
    val createdAt: Long = 0L,
    val updatedAt: Long = 0L,
    val version: Long = 0L
) {
    val totalBalance: Money get() = availableBalance + reservedBalance

    fun hasSufficientBalance(amount: Money): Boolean = availableBalance >= amount

    fun applyCredit(amount: Money): Wallet = copy(
        availableBalance = availableBalance + amount,
        version = version + 1
    )

    fun applyDebit(amount: Money): Wallet? {
        if (!hasSufficientBalance(amount)) return null
        return copy(
            availableBalance = availableBalance - amount,
            version = version + 1
        )
    }

    fun applyHold(amount: Money): Wallet? {
        if (!hasSufficientBalance(amount)) return null
        return copy(
            availableBalance = availableBalance - amount,
            reservedBalance = reservedBalance + amount,
            version = version + 1
        )
    }

    fun applyRelease(amount: Money): Wallet? {
        if (reservedBalance < amount) return null
        return copy(
            availableBalance = availableBalance + amount,
            reservedBalance = reservedBalance - amount,
            version = version + 1
        )
    }

    fun applyCapture(amount: Money): Wallet? {
        if (reservedBalance < amount) return null
        return copy(
            reservedBalance = reservedBalance - amount,
            version = version + 1
        )
    }
}

/**
 * Transaction Enums and Data Class
 */
// TransactionType extended for all usages
enum class TransactionType {
    PURCHASE, TOPUP, REFUND, ADJUSTMENT, HOLD, RELEASE, CAPTURE, WITHDRAWAL
}

// TransactionStatus extended for all usages
enum class TransactionStatus {
    PENDING, SUCCEEDED, FAILED, CANCELLED
}

// ReferenceType for transaction linkage
enum class ReferenceType {
    PLAN_ID,
    ORDER_ID,
    REFUND_ID,
    NONE
}

data class WalletTransaction(
    val id: String,
    val userId: String,
    val type: TransactionType,
    val status: TransactionStatus,
    val amount: Money,
    val balanceAfter: Money? = null,
    val referenceType: ReferenceType? = null,
    val referenceId: String? = null,
    val idempotencyKey: String,
    val description: String? = null,
    val metadata: Map<String, String> = emptyMap(),
    val createdAt: Long,
    val updatedAt: Long
) {
    fun isCredit(): Boolean = type == TransactionType.TOPUP || type == TransactionType.REFUND
    fun isDebit(): Boolean = type == TransactionType.PURCHASE || type == TransactionType.ADJUSTMENT
}

// Helper extension for Money to get decimal string
fun Money.toDecimalString(): String {
    val rupeesPart = minorUnits / 100
    val paisePart = (minorUnits % 100).toString().padStart(2, '0')
    return "$rupeesPart.$paisePart"
}

/**
 * WalletOperationResult for repository operations
 */
sealed class WalletOperationResult {
    data class Success(val transaction: WalletTransaction, val wallet: Wallet) : WalletOperationResult()
    data class Failure(val error: WalletError) : WalletOperationResult()
}

/**
 * WalletError for error handling
 */
sealed class WalletError {
    object InsufficientFunds : WalletError()
    object WalletNotFound : WalletError()
    object TransactionNotFound : WalletError()
    object InvalidAmount : WalletError()
    object DuplicateTransaction : WalletError()
    data class NetworkError(val message: String) : WalletError()
    data class UnknownError(val message: String) : WalletError()
}
